import { formatBytes } from '../lib/emitter.ts';
import { LEVELS, levelLabel, storagePerHour, type QualityMode } from '../lib/quality.ts';
import type { StudioEngine } from '../lib/studio.ts';
import { Icon, useObservable } from './common.tsx';

const MODES: { id: QualityMode; label: string; hint: string }[] = [
  { id: 'maximum', label: 'Maximum', hint: 'Every camera and screen at the best it delivers, up to 4K. Nothing is stepped down.' },
  { id: 'smart', label: 'Smart', hint: 'Full HD for everything, stepped down only as far as this computer needs: screens first, your main camera last.' },
  { id: 'light', label: 'Light', hint: 'Small files and little load, for a slow computer or little free space.' },
];

const VERDICT = {
  comfortable: { text: 'Comfortable for this computer', cls: 'ok' },
  tight: { text: 'Near this computer’s limit', cls: 'warn' },
  over: { text: 'Likely more than this computer can keep up with', cls: 'bad' },
} as const;

/**
 * Recording quality: the mode, what each camera and screen will record at, and what that means in space and load
 * for this computer. Any source can be set by hand.
 */
export function QualityCard({ engine }: { engine: StudioEngine }) {
  const sm = engine.sources;
  useObservable(sm);
  const hw = engine.hardware;
  if (!hw)
    return (
      <div className="card row muted small">
        <span className="spinner" /> Measuring this computer’s encoder…
      </div>
    );
  const plan = sm.plan;
  const video = sm.active.filter((s) => plan.sources.has(s.id));
  const wavs = sm.active
    .filter((s) => (s.kind === 'microphone' || s.kind === 'system-audio') && s.use.record)
    .map((s) => ({ channels: s.kind === 'system-audio' ? (s.track.getSettings().channelCount ?? 2) : 1, bits: (sm.processing === 'studio' && s.kind === 'microphone' ? 24 : 16) as 16 | 24 }));
  const perHour = storagePerHour(plan, wavs);
  const hours = engine.storageFree != null && perHour > 0 ? engine.storageFree / perHour : null;
  const verdict = VERDICT[plan.verdict];
  const mode = MODES.find((m) => m.id === plan.mode)!;

  return (
    <div className="card quality-card">
      <div className="section-title">
        <h3>Recording quality</h3>
      </div>
      <div className="segmented" role="radiogroup" aria-label="Recording quality">
        {MODES.map((m) => (
          <button key={m.id} role="radio" aria-checked={plan.mode === m.id} className={plan.mode === m.id ? 'on' : ''} onClick={() => engine.setQualityMode(m.id)}>
            {m.label}
            {m.id === 'smart' ? <span className="faint"> · recommended</span> : null}
          </button>
        ))}
      </div>
      <p className="panel-note">{mode.hint}</p>

      {video.map((s) => {
        const p = plan.sources.get(s.id)!;
        const kind = s.kind as 'camera' | 'screen';
        const best = s.bestLevel ?? 3;
        return (
          <div key={s.id} className="quality-row">
            <Icon name={kind === 'camera' ? 'cam' : 'screen'} size={15} />
            <span className="grow ellipsis">{s.label}</span>
            {p.how === 'recording' ? (
              <span className="faint small" title="A file can’t change size halfway; this applies from the next take">
                {levelLabel(p.level)} · recording
              </span>
            ) : (
              <select
                className="sm"
                aria-label={`${s.label} quality`}
                value={p.how === 'chosen' ? p.index : 'auto'}
                onChange={(e) => sm.choose(s.id, e.target.value === 'auto' ? null : Number(e.target.value))}
              >
                <option value="auto">
                  {p.how === 'auto' ? `${levelLabel(p.level)}${p.steppedDown ? ' (stepped down to fit)' : ' (automatic)'}` : 'Automatic'}
                </option>
                {LEVELS[kind].map((l, i) =>
                  i < best ? null : (
                    <option key={i} value={i}>
                      {levelLabel(l)}
                    </option>
                  ),
                )}
              </select>
            )}
          </div>
        );
      })}

      <div className={`alert small ${verdict.cls === 'ok' ? 'info' : verdict.cls}`}>
        <Icon name={verdict.cls === 'ok' ? 'check' : 'warn'} size={14} />
        <span>
          {verdict.text}
          {video.length > 0 && <> · about {formatBytes(perHour)} per hour</>}
          {hours != null && <> · room for {hours >= 10 ? Math.floor(hours) : hours.toFixed(1)} hours here</>}
          {plan.verdict === 'over' && (plan.mode === 'maximum' ? '. Smart steps down only what it must.' : '. Lower a source, or remove one.')}
        </span>
      </div>

      {sm.tested?.result === 'clean' && plan.load <= sm.tested.load + 0.01 && (
        <div className="alert small info">
          <Icon name="check" size={14} /> The 6-second test recorded these settings cleanly on this computer.
        </div>
      )}
      {sm.tested?.result === 'struggled' && (
        <div className="alert small warn">
          <Icon name="warn" size={14} />
          <span>
            In the 6-second test this computer fell behind.{' '}
            {plan.mode === 'smart' ? 'Smart has stepped down to fit; test again to check.' : 'Smart would step down only what it must.'}
          </span>
        </div>
      )}

      <dl className="kv">
        <dt>Video</dt>
        <dd>
          {hw.videoCodec?.toUpperCase() ?? 'No video encoder'}
          {hw.encodeFps720 ? ` · ${hw.encodeFps720} fps encode` : ''}
        </dd>
        <dt>Audio</dt>
        <dd>WAV, lossless</dd>
        <dt>Processor</dt>
        <dd>{hw.cores} cores</dd>
        {engine.storageFree != null && (
          <>
            <dt>Free for recordings</dt>
            <dd>{formatBytes(engine.storageFree)}</dd>
          </>
        )}
      </dl>
      {hw.notes.map((n) => (
        <div key={n} className="alert warn small">
          <Icon name="warn" size={14} /> {n}
        </div>
      ))}
    </div>
  );
}
